import { useCallback, useEffect, useState } from "react";
import { aiAgentService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Loader2, Send } from "lucide-react";
import {
  AgentIcon, RiskBadge, useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, fmtDate,
} from "./shared";

const STATUSES = ["pending", "approved", "rejected"];

function ApprovalCard({ ap, onDecide, busy }) {
  const [note, setNote] = useState("");
  const isPending = ap.status === "pending";
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={`approval-card-${ap.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
          <AgentIcon code={ap.agentCode} className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-sm font-bold text-foreground">{ap.title}</h3>
              {ap.entityLabel && <p className="text-xs text-muted-foreground">Target: {ap.entityLabel}</p>}
            </div>
            <RiskBadge level={ap.riskLevel} />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{ap.reason}</p>
          <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-2"><Send className="h-3 w-3" /> High-risk action requires your sign-off. External sends are SIMULATED & logged.</p>

          {isPending ? (
            <div className="mt-3 space-y-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Decision note (optional)"
                className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-foreground" data-testid={`approval-note-${ap.id}`} />
              <div className="flex items-center gap-2">
                <button onClick={() => onDecide(ap, true, note)} disabled={busy === ap.id}
                  className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid={`approval-approve-${ap.id}`}>
                  {busy === ap.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve & execute
                </button>
                <button onClick={() => onDecide(ap, false, note)} disabled={busy === ap.id}
                  className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10 disabled:opacity-50" data-testid={`approval-reject-${ap.id}`}>
                  <X className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2 text-[11px] text-muted-foreground" data-testid={`approval-decided-${ap.id}`}>
              <span className={`font-semibold capitalize ${ap.status === "approved" ? "text-emerald-600" : "text-red-600"}`}>{ap.status}</span>
              {ap.decisionNote ? ` · "${ap.decisionNote}"` : ""} · {fmtDate(ap.decidedAt)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ApprovalsPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setItems([]); return; }
    setLoading(true);
    aiAgentService.approvals({ ...params, status })
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params, status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (ap, approve, note) => {
    setBusy(ap.id);
    try {
      const fn = approve ? aiAgentService.approve : aiAgentService.reject;
      const r = await fn(ap.id, { note: note || undefined });
      if (approve) {
        const ex = r.execution;
        toast.success(ex?.simulated ? "Approved — external send simulated & logged" : "Approved & executed");
      } else {
        toast.success("Rejected");
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="approvals-page">
      <PageHeader icon={ShieldCheck} title="Approvals Queue"
        subtitle="High-risk actions wait here for explicit human approval before anything happens.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : (
        <>
          <div className="flex bg-muted/40 rounded-lg p-1 mb-4 w-fit" data-testid="approval-status-tabs">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${status === s ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`approval-tab-${s}`}>{s}</button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="approval-empty">
              <ShieldCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No {status} approvals.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="approval-list">
              {items.map((ap) => <ApprovalCard key={ap.id} ap={ap} onDecide={decide} busy={busy} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
